/*
 *
 *  * Copyright (c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Pipe, PipeTransform } from '@angular/core';
import { GenesisMicroAppAction, MicroAppStatus } from '@app/di/channel/window/postmessage.types';
import { appConstants } from '@app/common/app-constants';
import { TranslateService } from '@ngx-translate/core';
import { MicroAppBase } from '@app/di/channel/microapp/micro-app-base';

// Status configuration interface
interface StatusConfig {
  description: string;
  label?: string;
  icon?: string;
  iconColor?: string;
}

@Pipe({
  name: 'microAppNotification',
  pure: true
})
export class MicroAppNotificationPipe implements PipeTransform {
  private microAppConfigurations = new Map<string, Map<string, StatusConfig>>();

  constructor(private translate: TranslateService) {
    this.initializeConfigurations();
  }

  /**
   * Transform method to get icon, iconColor, or description for a microapp status
   * @param status The status value
   * @param args [`appId`: string, `type`: 'icon','iconColor', 'description']
   * @returns The corresponding icon, color, or description string
   */
  transform(microapp: MicroAppBase): StatusConfig | undefined {
    if (!microapp || !microapp.appID) {
      return undefined;
    }

    // Get the configuration for this microapp
    const appConfig = this.getMicroAppConfig(microapp.appID);
    if (!appConfig) {
      return null;
    }

    // Get the notification map configured with status
    // Check if status.status exists, otherwise fall back to type
    const statusKey = microapp.statusValue?.status?.status || microapp.statusValue?.type;

    if (!statusKey) {
      return null;
    }
    const notificationConfig = appConfig.get(statusKey);
    if (!notificationConfig) {
      return null;
    }

    return {
      ...notificationConfig,
      label: this.translate.instant(`di.microapp.label.${microapp.appID.toLowerCase()}`)
    };
  }

  /**
   * Get microapp configuration, falling back to default genesis configuration
   * @param appId The microapp ID
   * @returns The microapp configuration or default
   */
  private getMicroAppConfig(appId: string): Map<string, StatusConfig> {
    // Try to get specific configuration for this app
    const config = this.microAppConfigurations.get(appId);

    // Fall back to default configurations
    if (!config) {
      return null;
    }

    return config;
  }

  /**
   * Initialize configurations for different microapps
   */
  private initializeConfigurations(): void {
    const completedConfig: StatusConfig = {
      icon: 'ico-checkmark-circle-16',
      iconColor: '#26842F',
      description: this.translate.instant('di.microapp.msg.completed')
    };
    const errorConfig: StatusConfig = {
      icon: 'ico-error-16',
      iconColor: '#b05f09',
      description: this.translate.instant('di.microapp.msg.error')
    };
    const readyConfig: StatusConfig = {
      icon: 'ico-error-16',
      iconColor: '#B05F09',
      description: this.translate.instant('di.microapp.msg.ready')
    };
    const inProgressConfig: StatusConfig = {
      icon: 'ico-spinner-16',
      description: this.translate.instant('di.microapp.msg.inprogress')
    };

    const genesisMicroAppConfig = new Map<string, StatusConfig>([
      // Ready
      [GenesisMicroAppAction.READY, readyConfig],
      [GenesisMicroAppAction.MINIMIZE_NO_ACTION, readyConfig],
      [MicroAppStatus.NOT_STARTED, readyConfig],

      // In Progress
      [GenesisMicroAppAction.STARTED_WITH_ACTION_IMPORT, inProgressConfig],
      [GenesisMicroAppAction.STARTED_WITH_ACTION_EXPORT, inProgressConfig],
      [GenesisMicroAppAction.MINIMIZE_WITH_ACTION, inProgressConfig],
      [MicroAppStatus.STARTED, inProgressConfig],

      // Completed
      [GenesisMicroAppAction.CLOSE_WITH_ACTION, completedConfig],
      [MicroAppStatus.DONE, completedConfig],

      // Error
      [GenesisMicroAppAction.REOPEN_ERROR, errorConfig],
      [MicroAppStatus.ERROR, errorConfig]
    ]);

    this.microAppConfigurations.set(appConstants.APP_ID.IMPORT, genesisMicroAppConfig);
    this.microAppConfigurations.set(appConstants.APP_ID.EXPORT, genesisMicroAppConfig);
  }
}
